import React, { useState, useEffect } from 'react';
import { Settings, Save, X, Plus, Trash2, RotateCcw, Edit2, Check, Tag } from 'lucide-react';
import { urgencyRulesAPI } from '../../services/api';

const defaultLevels = ['Low', 'Medium', 'High', 'Very High'];

const defaultRules = [
  { min_days: 0, max_days: 90, urgency: 'Low' },
  { min_days: 90, max_days: 180, urgency: 'Medium' },
  { min_days: 180, max_days: 365, urgency: 'High' },
  { min_days: 365, max_days: null, urgency: 'Very High' },
];

// Color cycle for urgency levels (assigned by index, escalating from cool to warm)
const levelColorsByIndex = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-yellow-100 text-yellow-700 border-yellow-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-red-100 text-red-700 border-red-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-emerald-100 text-emerald-700 border-emerald-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
];

const UrgencyRulesConfig = ({ isOpen, onClose, onSaved }) => {
  const [levels, setLevels] = useState([...defaultLevels]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editLevelValue, setEditLevelValue] = useState('');
  const [newLevelName, setNewLevelName] = useState('');
  const [showAddLevel, setShowAddLevel] = useState(false);

  useEffect(() => {
    if (isOpen) loadRules();
  }, [isOpen]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await urgencyRulesAPI.get();
      const loadedLevels = res.data.urgency_levels || [...defaultLevels];
      let loadedRules = res.data.rules || [...defaultRules];

      // Auto-migrate: remap any rule referencing a level not in the current levels list
      loadedRules = loadedRules.map(rule => {
        if (!loadedLevels.includes(rule.urgency)) {
          return { ...rule, urgency: loadedLevels[0] || 'Low' };
        }
        return rule;
      });

      setLevels(loadedLevels);
      setRules(loadedRules);
    } catch {
      setLevels([...defaultLevels]);
      setRules([...defaultRules]);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (levelName) => {
    const idx = levels.indexOf(levelName);
    if (idx === -1) return 'bg-gray-100 text-gray-600 border-gray-200';
    return levelColorsByIndex[idx % levelColorsByIndex.length];
  };

  const handleSave = async () => {
    if (levels.length === 0) {
      alert('At least one urgency level is required');
      return;
    }
    // Validate rules
    const sorted = [...rules].sort((a, b) => a.min_days - b.min_days);
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].min_days < 0) { alert('Days cannot be negative'); return; }
      if (sorted[i].max_days !== null && sorted[i].max_days <= sorted[i].min_days) {
        alert(`Rule "${sorted[i].urgency}": max days must be greater than min days`);
        return;
      }
      if (!levels.includes(sorted[i].urgency)) {
        alert(`Rule references unknown urgency level "${sorted[i].urgency}". Please fix or remove the rule.`);
        return;
      }
    }

    setSaving(true);
    try {
      await urgencyRulesAPI.update({ urgency_levels: levels, rules: sorted });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to save urgency rules:', err);
      alert('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  // --- Level management ---
  const startEditLevel = (idx) => {
    setEditingLevel(idx);
    setEditLevelValue(levels[idx]);
  };

  const saveEditLevel = (idx) => {
    const trimmed = editLevelValue.trim();
    if (!trimmed) return;
    if (levels.some((l, i) => i !== idx && l.toLowerCase() === trimmed.toLowerCase())) {
      alert('This level name already exists');
      return;
    }
    const oldName = levels[idx];
    setLevels(prev => prev.map((l, i) => i === idx ? trimmed : l));
    // Update rules that reference the old name
    setRules(prev => prev.map(r => r.urgency === oldName ? { ...r, urgency: trimmed } : r));
    setEditingLevel(null);
    setEditLevelValue('');
  };

  const addLevel = () => {
    const trimmed = newLevelName.trim();
    if (!trimmed) return;
    if (levels.some(l => l.toLowerCase() === trimmed.toLowerCase())) {
      alert('This level name already exists');
      return;
    }
    setLevels(prev => [...prev, trimmed]);
    setNewLevelName('');
    setShowAddLevel(false);
  };

  const removeLevel = (idx) => {
    const name = levels[idx];
    const usedInRules = rules.some(r => r.urgency === name);
    if (usedInRules) {
      alert(`Cannot remove "${name}" — it is used in a rule. Remove or reassign the rule first.`);
      return;
    }
    if (levels.length <= 1) {
      alert('At least one urgency level is required');
      return;
    }
    setLevels(prev => prev.filter((_, i) => i !== idx));
  };

  // --- Rule management ---
  const updateRule = (index, field, value) => {
    setRules(prev => {
      const updated = [...prev];
      if (field === 'min_days' || field === 'max_days') {
        updated[index] = { ...updated[index], [field]: value === '' || value === null ? null : parseInt(value, 10) };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const addRule = () => {
    const lastRule = rules[rules.length - 1];
    const newMin = lastRule?.max_days || (lastRule?.min_days || 0) + 30;
    setRules(prev => [...prev, { min_days: newMin, max_days: null, urgency: levels[0] || 'Low' }]);
  };

  const removeRule = (index) => {
    if (rules.length <= 1) return;
    setRules(prev => prev.filter((_, i) => i !== index));
  };

  const resetToDefaults = () => {
    setLevels([...defaultLevels]);
    setRules([...defaultRules]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="urgency-rules-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <div className="flex items-center gap-2">
            <Settings size={20} className="text-gray-600" />
            <h2 className="text-lg font-bold text-gray-900">Coverage Urgency Configuration</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-urgency-rules">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              {/* Section 1: Urgency Levels */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <Tag size={15} />
                  Urgency Levels
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Define the urgency level names used across your investor contacts. Levels are ordered from lowest to highest severity.
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {levels.map((level, idx) => (
                    <div key={idx} className={`inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-full border ${getLevelColor(level)}`} data-testid={`urgency-level-${idx}`}>
                      {editingLevel === idx ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editLevelValue}
                            onChange={e => setEditLevelValue(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && saveEditLevel(idx)}
                            className="w-24 bg-transparent border-b border-current text-sm outline-none px-0"
                            autoFocus
                            data-testid={`edit-level-input-${idx}`}
                          />
                          <button onClick={() => saveEditLevel(idx)} className="p-0.5 hover:opacity-70">
                            <Check size={14} />
                          </button>
                          <button onClick={() => setEditingLevel(null)} className="p-0.5 hover:opacity-70">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span>{level}</span>
                          <button onClick={() => startEditLevel(idx)} className="p-0.5 hover:opacity-70" data-testid={`edit-level-btn-${idx}`}>
                            <Edit2 size={12} />
                          </button>
                          <button onClick={() => removeLevel(idx)} className="p-0.5 hover:opacity-70" data-testid={`remove-level-btn-${idx}`}>
                            <X size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}

                  {showAddLevel ? (
                    <div className="inline-flex items-center gap-1 bg-gray-50 border border-dashed border-gray-300 rounded-full px-3 py-1.5">
                      <input
                        type="text"
                        value={newLevelName}
                        onChange={e => setNewLevelName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addLevel()}
                        placeholder="Level name"
                        className="w-24 bg-transparent text-sm outline-none"
                        autoFocus
                        data-testid="new-level-input"
                      />
                      <button onClick={addLevel} className="p-0.5 text-green-600 hover:text-green-800">
                        <Check size={14} />
                      </button>
                      <button onClick={() => { setShowAddLevel(false); setNewLevelName(''); }} className="p-0.5 text-gray-400 hover:text-gray-600">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddLevel(true)}
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 border border-dashed border-blue-300 rounded-full px-3 py-1.5"
                      data-testid="add-level-btn"
                    >
                      <Plus size={14} />
                      Add Level
                    </button>
                  )}
                </div>
              </div>

              {/* Section 2: Day-Range Rules */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <Settings size={15} />
                  Day-Range Rules
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Assign an urgency level based on the number of days since the last interaction with an investor.
                </p>

                <div className="border rounded-lg overflow-hidden mb-3">
                  <table className="w-full" data-testid="urgency-rules-table">
                    <thead>
                      <tr className="bg-gray-50 text-left text-sm font-medium text-gray-600">
                        <th className="px-4 py-2.5">From (days)</th>
                        <th className="px-4 py-2.5">To (days)</th>
                        <th className="px-4 py-2.5">Urgency Level</th>
                        <th className="px-4 py-2.5 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.sort((a, b) => a.min_days - b.min_days).map((rule, index) => (
                        <tr key={index} className="border-t" data-testid={`urgency-rule-row-${index}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400 text-xs">&ge;</span>
                              <input
                                type="number"
                                min="0"
                                value={rule.min_days}
                                onChange={e => updateRule(index, 'min_days', e.target.value)}
                                className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                                data-testid={`rule-min-days-${index}`}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400 text-xs">&lt;</span>
                              {rule.max_days === null ? (
                                <span className="text-sm text-gray-500 italic">No limit</span>
                              ) : (
                                <input
                                  type="number"
                                  min="1"
                                  value={rule.max_days || ''}
                                  onChange={e => updateRule(index, 'max_days', e.target.value || null)}
                                  className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                                  data-testid={`rule-max-days-${index}`}
                                />
                              )}
                              {rule.max_days !== null ? (
                                <button onClick={() => updateRule(index, 'max_days', null)} className="text-xs text-blue-500 hover:text-blue-700 ml-1">
                                  No limit
                                </button>
                              ) : (
                                <button onClick={() => updateRule(index, 'max_days', rule.min_days + 30)} className="text-xs text-blue-500 hover:text-blue-700 ml-1">
                                  Set limit
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <select
                              value={rule.urgency}
                              onChange={e => updateRule(index, 'urgency', e.target.value)}
                              className={`text-sm font-medium px-3 py-1.5 rounded-full border ${getLevelColor(rule.urgency)}`}
                              data-testid={`rule-urgency-${index}`}
                            >
                              {levels.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2.5">
                            <button
                              onClick={() => removeRule(index)}
                              disabled={rules.length <= 1}
                              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30"
                              data-testid={`remove-rule-${index}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-3">
                  <button onClick={addRule} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800" data-testid="add-rule-btn">
                    <Plus size={16} className="mr-1" />
                    Add Rule
                  </button>
                  <button onClick={resetToDefaults} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700" data-testid="reset-rules-btn">
                    <RotateCcw size={14} className="mr-1" />
                    Reset to Defaults
                  </button>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-50 border rounded-lg p-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Preview</p>
                <div className="flex flex-wrap gap-2">
                  {rules.sort((a, b) => a.min_days - b.min_days).map((rule, i) => (
                    <span key={i} className={`text-xs px-2.5 py-1 rounded-full border ${getLevelColor(rule.urgency)}`}>
                      {rule.min_days}d {rule.max_days ? `- ${rule.max_days}d` : '+'} = {rule.urgency}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm inline-flex items-center"
            data-testid="save-urgency-rules-btn"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UrgencyRulesConfig;
